import { Product, type SupportedLocale } from './intl-types';
import { isLocaleSupported } from './is-locale-supported';
import db_cs_CZ from './locales/databases/cs-CZ.json?url';
import db_da_DK from './locales/databases/da-DK.json?url';
import db_de_DE from './locales/databases/de-DE.json?url';
import db_en_GB from './locales/databases/en-GB.json?url';
import db_en_US from './locales/databases/en-US.json?url';
import db_en_ZZ from './locales/databases/en-ZZ.json?url';
import db_en_pseudo from './locales/databases/en-pseudo.json?url';
import db_en from './locales/databases/en.json?url';
import db_es from './locales/databases/es.json?url';
import db_fi_FI from './locales/databases/fi-FI.json?url';
import db_fr_FR from './locales/databases/fr-FR.json?url';
import db_hu_HU from './locales/databases/hu-HU.json?url';
import db_it_IT from './locales/databases/it-IT.json?url';
import db_ja_JP from './locales/databases/ja-JP.json?url';
import db_ko_KR from './locales/databases/ko-KR.json?url';
import db_nb_NO from './locales/databases/nb-NO.json?url';
import db_nl_NL from './locales/databases/nl-NL.json?url';
import db_pl_PL from './locales/databases/pl-PL.json?url';
import db_pt_BR from './locales/databases/pt-BR.json?url';
import db_ru_RU from './locales/databases/ru-RU.json?url';
import db_sv_SE from './locales/databases/sv-SE.json?url';
import db_tr_TR from './locales/databases/tr-TR.json?url';
import db_xx from './locales/databases/xx.json?url';
import db_zh_CN from './locales/databases/zh-CN.json?url';
import db_zh_TW from './locales/databases/zh-TW.json?url';
import wb_cs_CZ from './locales/whiteboards/cs-CZ.json.br?url';
import wb_da_DK from './locales/whiteboards/da-DK.json.br?url';
import wb_de_DE from './locales/whiteboards/de-DE.json.br?url';
import wb_en_GB from './locales/whiteboards/en-GB.json.br?url';
import wb_en_US from './locales/whiteboards/en-US.json.br?url';
import wb_en_ZZ from './locales/whiteboards/en-ZZ.json.br?url';
import wb_en_pseudo from './locales/whiteboards/en-pseudo.json.br?url';
import wb_en from './locales/whiteboards/en.json.br?url';
import wb_es from './locales/whiteboards/es.json.br?url';
import wb_fi_FI from './locales/whiteboards/fi-FI.json.br?url';
import wb_fr_FR from './locales/whiteboards/fr-FR.json.br?url';
import wb_hu_HU from './locales/whiteboards/hu-HU.json.br?url';
import wb_it_IT from './locales/whiteboards/it-IT.json.br?url';
import wb_ja_JP from './locales/whiteboards/ja-JP.json.br?url';
import wb_ko_KR from './locales/whiteboards/ko-KR.json.br?url';
import wb_nb_NO from './locales/whiteboards/nb-NO.json.br?url';
import wb_nl_NL from './locales/whiteboards/nl-NL.json.br?url';
import wb_pl_PL from './locales/whiteboards/pl-PL.json.br?url';
import wb_pt_BR from './locales/whiteboards/pt-BR.json.br?url';
import wb_ru_RU from './locales/whiteboards/ru-RU.json.br?url';
import wb_sv_SE from './locales/whiteboards/sv-SE.json.br?url';
import wb_tr_TR from './locales/whiteboards/tr-TR.json.br?url';
import wb_xx from './locales/whiteboards/xx.json.br?url';
import wb_zh_CN from './locales/whiteboards/zh-CN.json.br?url';
import wb_zh_TW from './locales/whiteboards/zh-TW.json.br?url';

const unreachable = (value: never) => {
  throw new Error(`Not implemented: ${value}`);
};

/**
 * Given a user's locale, returns the URL to lazily load from
 */
export function getUrlFromLocale(
  locale = 'en-US',
  product: Product = Product.WHITEBOARDS
) {
  switch (product) {
    case Product.WHITEBOARDS:
    case Product.DATABASES:
      return getUrlFromProductLocale(locale, product);
    default:
      return unreachable(product);
  }
}

/**
 * Defines a map for whiteboards between a locale and the vite URL to load from
 * When adding a new locale, make sure to add it to the supportedLocale array
 * in {@link ./intl-types.ts}
 */
const whiteBoardsLocaleToUrlMap: Record<SupportedLocale, string> = {
  'en-GB': wb_en_GB,
  'en-US': wb_en_US,
  de: wb_de_DE,
  cs: wb_cs_CZ,
  ko: wb_ko_KR,
  'pt-BR': wb_pt_BR,
  fr: wb_fr_FR,
  es: wb_es,
  it: wb_it_IT,
  ja: wb_ja_JP,
  nl: wb_nl_NL,
  nb: wb_nb_NO,
  da: wb_da_DK,
  sv: wb_sv_SE,
  fi: wb_fi_FI,
  ru: wb_ru_RU,
  pl: wb_pl_PL,
  tr: wb_tr_TR,
  hu: wb_hu_HU,
  'zh-CN': wb_zh_CN,
  'zh-TW': wb_zh_TW,
  'en-pseudo': wb_en_pseudo,
  xx: wb_xx,
  en: wb_en,
  'en-ZZ': wb_en_ZZ,
};

/**
 * Defines a map for databases between a locale and the vite URL to load from
 * When adding a new locale, make sure to add it to the supportedLocale array
 * in {@link ./intl-types.ts}
 */
const databasesLocaleToUrlMap: Record<SupportedLocale, string> = {
  'en-GB': db_en_GB,
  'en-US': db_en_US,
  de: db_de_DE,
  cs: db_cs_CZ,
  ko: db_ko_KR,
  'pt-BR': db_pt_BR,
  fr: db_fr_FR,
  es: db_es,
  it: db_it_IT,
  ja: db_ja_JP,
  nl: db_nl_NL,
  nb: db_nb_NO,
  da: db_da_DK,
  sv: db_sv_SE,
  fi: db_fi_FI,
  ru: db_ru_RU,
  pl: db_pl_PL,
  tr: db_tr_TR,
  hu: db_hu_HU,
  'zh-CN': db_zh_CN,
  'zh-TW': db_zh_TW,
  'en-pseudo': db_en_pseudo,
  xx: db_xx,
  en: db_en,
  'en-ZZ': db_en_ZZ,
};

const getUrlFromProductLocale = (
  locale: string,
  product: Product = Product.WHITEBOARDS
) => {
  const localeToLoad = isLocaleSupported(locale) ? locale : 'en-US';
  return product === Product.WHITEBOARDS
    ? whiteBoardsLocaleToUrlMap[localeToLoad]
    : databasesLocaleToUrlMap[localeToLoad];
};
